import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { RunView } from '@memberjunction/core';
import { MJArtifactVersionEntity } from '@memberjunction/core-entities';
import { IzzyChannelMessageAttachmentEntity } from 'mj_generatedentities';

/**
 * One row in the chip list, denormalized from the join row + latest ArtifactVersion.
 * The ID we expose on click is the AttachmentID (the join row), not the ArtifactID,
 * so the drawer can resolve siblings (prev/next) from the same source list.
 */
export interface MessageAttachmentChip {
    AttachmentID: string;
    ArtifactID: string;
    Filename: string;
    MimeType: string;
    SizeBytes: number;
    DisplayOrder: number;
}

/**
 * Renders a chip strip of attachments below an inbound message, mimicking
 * Outlook's attachment row. Click → emits (open) so a parent drawer can
 * render the artifact viewer. Renders nothing when the message has no
 * attachments.
 */
@Component({
    standalone: false,
    selector: 'app-message-attachments',
    templateUrl: './message-attachments.component.html',
    styleUrls: ['./message-attachments.component.css']
})
export class MessageAttachmentsComponent implements OnChanges {
    @Input() messageId: string | null = null;

    /** When set, only attachments with this Direction are shown. Null loads all (legacy callers). */
    @Input() direction: 'Inbound' | 'Outbound' | null = null;

    /** Overrides the default "Attachments (N)" header text (e.g. "Sent with this reply"). */
    @Input() label: string | null = null;

    /** 'cardFooter' restyles the strip as a full-bleed footer band inside a card. */
    @Input() variant: 'default' | 'cardFooter' = 'default';

    @Output() open = new EventEmitter<{ chip: MessageAttachmentChip; index: number; chips: MessageAttachmentChip[] }>();

    protected chips: MessageAttachmentChip[] = [];
    protected isLoading = false;
    protected errorMessage: string | null = null;

    /** Monotonic counter; each load() takes a snapshot and only commits if it matches at completion. */
    private _loadToken = 0;

    constructor(private cdr: ChangeDetectorRef) {}

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes['messageId'] || changes['direction']) {
            this.chips = [];
            if (this.messageId) {
                await this.loadAttachments();
            }
        }
    }

    private async loadAttachments(): Promise<void> {
        if (!this.messageId) return;
        const myToken = ++this._loadToken;
        const myMessageId = this.messageId;
        this.isLoading = true;
        this.errorMessage = null;
        try {
            const rv = new RunView();

            // 1. Load join rows for this message, scoped to the requested direction so
            //    staff-attached outbound files never render as the inbound message's own
            const directionFilter = this.direction ? ` AND Direction='${this.direction}'` : '';
            const joinResult = await rv.RunView<IzzyChannelMessageAttachmentEntity>({
                EntityName: 'Channel Message Attachments',
                ExtraFilter: `ChannelMessageID='${this.escape(myMessageId)}'${directionFilter}`,
                OrderBy: 'DisplayOrder ASC',
                ResultType: 'simple'
            });

            // Bail out if a newer load has started while we awaited.
            if (myToken !== this._loadToken) return;

            if (!joinResult.Success || !joinResult.Results || joinResult.Results.length === 0) {
                this.chips = [];
                this.cdr.markForCheck();
                return;
            }

            const joins = joinResult.Results;
            const artifactIds = joins.map(j => j.ArtifactID);

            // 2. Load the latest ArtifactVersion for each artifact in one batched RunView.
            //    We sort DESC and pick the first per artifact in client-side merge.
            const artifactIdList = artifactIds.map(id => `'${this.escape(id)}'`).join(',');
            const versionResult = await rv.RunView<MJArtifactVersionEntity>({
                EntityName: 'MJ: Artifact Versions',
                ExtraFilter: `ArtifactID IN (${artifactIdList})`,
                OrderBy: 'ArtifactID, VersionNumber DESC',
                ResultType: 'simple'
            });

            // Bail out if a newer load has started while we awaited.
            if (myToken !== this._loadToken) return;

            const latestByArtifact = new Map<string, MJArtifactVersionEntity>();
            if (versionResult.Success && versionResult.Results) {
                for (const v of versionResult.Results) {
                    if (!latestByArtifact.has(v.ArtifactID)) {
                        latestByArtifact.set(v.ArtifactID, v);
                    }
                }
            }

            this.chips = joins.map(j => {
                const v = latestByArtifact.get(j.ArtifactID);
                return {
                    AttachmentID: j.ID,
                    ArtifactID: j.ArtifactID,
                    Filename: v?.FileName || v?.Name || 'Attachment',
                    MimeType: v?.MimeType || 'application/octet-stream',
                    SizeBytes: v?.ContentSizeBytes ?? 0,
                    DisplayOrder: j.DisplayOrder
                };
            });
            this.cdr.markForCheck();
        } catch (error) {
            // Only surface the error if we're still the latest in-flight load.
            if (myToken === this._loadToken) {
                console.error('MessageAttachments: Error loading attachments:', error);
                this.errorMessage = 'Failed to load attachments.';
                this.chips = [];
            }
        } finally {
            // Only flip the spinner off if we're still the latest in-flight load.
            if (myToken === this._loadToken) {
                this.isLoading = false;
            }
        }
    }

    protected handleChipClick(chip: MessageAttachmentChip, index: number): void {
        this.open.emit({ chip, index, chips: this.chips });
    }

    protected formatSize(bytes: number): string {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    protected iconClass(mime: string): string {
        const m = mime.toLowerCase();
        if (m.includes('pdf')) return 'fa-solid fa-file-pdf';
        if (m.includes('word') || m.includes('msword')) return 'fa-solid fa-file-word';
        if (m.includes('excel') || m.includes('spreadsheet')) return 'fa-solid fa-file-excel';
        if (m.includes('json')) return 'fa-solid fa-file-code';
        if (m.includes('xml')) return 'fa-solid fa-file-code';
        if (m.includes('html')) return 'fa-solid fa-file-code';
        if (m.startsWith('text/')) return 'fa-solid fa-file-lines';
        if (m.startsWith('image/')) return 'fa-solid fa-file-image';
        return 'fa-solid fa-file';
    }

    protected iconColorClass(mime: string): string {
        const m = mime.toLowerCase();
        if (m.includes('pdf')) return 'icon-pdf';
        if (m.includes('word') || m.includes('msword')) return 'icon-word';
        if (m.includes('excel') || m.includes('spreadsheet')) return 'icon-excel';
        if (m.includes('json') || m.includes('xml') || m.includes('html')) return 'icon-code';
        return 'icon-default';
    }

    private escape(s: string): string {
        return s.replace(/'/g, "''");
    }
}
